import { useState, useEffect, useCallback } from 'react';

interface CachedTrack {
  id: string;
  url: string;
  cachedAt: number;
}

const CACHE_NAME = 'juke-joint-audio-v1';
const MAX_CACHED_TRACKS = 10;
const CACHE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Hook for managing offline audio caching
 */
export function useAudioOffline() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [cachedTracks, setCachedTracks] = useState<CachedTrack[]>([]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load cached tracks list from localStorage
  useEffect(() => {
    const cached = localStorage.getItem('juke-joint-cached-tracks');
    if (cached) {
      try {
        const tracks = JSON.parse(cached) as CachedTrack[];
        // Filter out expired tracks
        const validTracks = tracks.filter(
          (t) => Date.now() - t.cachedAt < CACHE_EXPIRY_MS
        );
        setCachedTracks(validTracks);
        localStorage.setItem('juke-joint-cached-tracks', JSON.stringify(validTracks));
      } catch (e) {
        console.error('Failed to parse cached tracks:', e);
      }
    }
  }, []);

  // Cache a track for offline playback
  const cacheTrack = useCallback(async (trackId: string, audioUrl: string) => {
    if (!('caches' in window)) return false;

    try {
      const cache = await caches.open(CACHE_NAME);
      
      // Check if already cached
      const existing = await cache.match(audioUrl);
      if (existing) {
        return true;
      }

      // Fetch and cache the audio file
      const response = await fetch(audioUrl);
      if (!response.ok) throw new Error('Failed to fetch audio');
      
      await cache.put(audioUrl, response.clone());

      // Update cached tracks list
      const newCachedTrack: CachedTrack = {
        id: trackId,
        url: audioUrl,
        cachedAt: Date.now(),
      };

      setCachedTracks((prev) => {
        const updated = [...prev.filter((t) => t.id !== trackId), newCachedTrack];
        // Keep only the most recent tracks
        const trimmed = updated.slice(-MAX_CACHED_TRACKS);
        localStorage.setItem('juke-joint-cached-tracks', JSON.stringify(trimmed));
        return trimmed;
      });

      return true;
    } catch (e) {
      console.error('Failed to cache track:', e);
      return false;
    }
  }, []);

  // Check if a track is cached
  const isTrackCached = useCallback(
    (trackId: string) => {
      return cachedTracks.some((t) => t.id === trackId);
    },
    [cachedTracks]
  );

  // Get cached audio URL (returns original URL if online or not cached)
  const getCachedUrl = useCallback(
    async (trackId: string, originalUrl: string): Promise<string> => {
      if (isOnline) return originalUrl;

      if (!('caches' in window)) return originalUrl;

      try {
        const cache = await caches.open(CACHE_NAME);
        const response = await cache.match(originalUrl);
        
        if (response) {
          // Return blob URL for offline playback
          const blob = await response.blob();
          return URL.createObjectURL(blob);
        }
      } catch (e) {
        console.error('Failed to get cached track:', e);
      }

      return originalUrl;
    },
    [isOnline]
  );

  // Clear all cached tracks
  const clearCache = useCallback(async () => {
    if ('caches' in window) {
      await caches.delete(CACHE_NAME);
    }
    localStorage.removeItem('juke-joint-cached-tracks');
    setCachedTracks([]);
  }, []);

  return {
    isOnline,
    cachedTracks,
    cacheTrack,
    isTrackCached,
    getCachedUrl,
    clearCache,
  };
}
